import React, { useMemo } from "react";
import {
  Table,
  Typography,
  Space,
  Row,
  Col,
  Card,
  InputNumber,
  Tooltip,
  Button,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatUnit } from "../../../utils/constants";
import {
  BuildUpItem,
  GeoToberWallSpecification,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../../../mock/geo-tober-wall-build-up";
import { useDevMode } from "@/contexts/DevModeContext";

const { Text } = Typography;

interface GeoToberWallSectionBuildUpProps {
  specification: GeoToberWallSpecification;
  onSpecificationChange?: (updatedSpec: GeoToberWallSpecification) => void;
}

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

// A row is treated as "manual override" when its stored net does not match
// the auto-computed L × W × H value (rounded to 3 dp). The override flag is
// thus derived from the existing `net` field — no extra backend column needed.
const NET_EQUALITY_EPSILON = 0.0005;
const computeAutoNet = (item: BuildUpItem): number =>
  round3((item.length ?? 0) * (item.width ?? 0) * (item.height ?? 0));
const isOverridden = (item: BuildUpItem): boolean =>
  Math.abs(item.net - computeAutoNet(item)) > NET_EQUALITY_EPSILON;

const GeoToberWallSectionBuildUp: React.FC<GeoToberWallSectionBuildUpProps> = ({
  specification,
  onSpecificationChange,
}) => {
  const showColumnCodes = useDevMode();
  const items = specification.items;

  const updateItems = (updatedItems: BuildUpItem[]) => {
    if (!onSpecificationChange) return;
    const netTotal = updatedItems.reduce(
      (sum, item) => sum + Math.abs(item.total),
      0,
    );
    const localMarkupFactor =
      (1 + specification.ohpPercent / 100) *
      (1 + (specification.prelimsPercent ?? 0) / 100);
    const ohpAmount = netTotal * (specification.ohpPercent / 100);
    const grandTotal = netTotal * localMarkupFactor;
    onSpecificationChange({
      ...specification,
      items: updatedItems,
      netTotal,
      ohpAmount,
      grandTotal,
    });
  };

  // Edit a single dimension (length/width/height). If the row's current net
  // matches the auto-computed L × W × H (within epsilon), keep it synced;
  // otherwise the row is in manual-override mode and we leave net untouched.
  // Total always follows net × rate.
  const updateDimension = (
    itemId: string,
    field: "length" | "width" | "height",
    value: number | null,
  ) => {
    const next = items.map((item) => {
      if (item.id !== itemId) return item;
      const wasAuto = !isOverridden(item);
      const merged: BuildUpItem = { ...item, [field]: value ?? 0 };
      if (wasAuto) {
        merged.net = computeAutoNet(merged);
      }
      merged.total = merged.net * (merged.rate || 0);
      return merged;
    });
    updateItems(next);
  };

  // Edit net directly. The override state is now derived (net ≠ L×W×H) so
  // there is no flag to set — just write the new value and update total.
  const updateNet = (itemId: string, value: number | null) => {
    const next = items.map((item) => {
      if (item.id !== itemId) return item;
      const newNet = value ?? 0;
      return {
        ...item,
        net: newNet,
        total: newNet * (item.rate || 0),
      };
    });
    updateItems(next);
  };

  // Reset to auto — snap net back to L × W × H so the equality check sees
  // the row as no longer overridden.
  const clearOverride = (itemId: string) => {
    const next = items.map((item) => {
      if (item.id !== itemId) return item;
      const newNet = computeAutoNet(item);
      return {
        ...item,
        net: newNet,
        total: newNet * (item.rate || 0),
      };
    });
    updateItems(next);
  };

  const markupFactor =
    (1 + specification.ohpPercent / 100) *
    (1 + (specification.prelimsPercent ?? 0) / 100);

  // Markup (prelims + OH&P) is baked into the rate
  const calculatedTotals = useMemo(() => {
    const grandTotal = items.reduce(
      (sum, item) => sum + Math.abs(item.net * item.rate * markupFactor),
      0,
    );
    return { grandTotal };
  }, [items, markupFactor]);

  const codeMap =
    specification.type === "geowall"
      ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
      : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: showColumnCodes ? "26%" : "34%",
      render: (text: string) => <span className="font-medium">{text}</span>,
    },
    ...(showColumnCodes
      ? [
          {
            title: "Code",
            dataIndex: "id",
            key: "code",
            width: "8%",
            align: "center" as const,
            render: (id: string) => {
              const code = codeMap[id];
              return code ? (
                <span
                  style={{
                    fontSize: 11,
                    color: "#999",
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                  }}
                >
                  {code}
                </span>
              ) : (
                <span style={{ color: "#ccc" }}>—</span>
              );
            },
          },
        ]
      : []),
    {
      title: "Length",
      dataIndex: "length",
      key: "length",
      width: "9%",
      align: "center" as const,
      render: (value: number | undefined, record: BuildUpItem) => (
        <InputNumber
          size="small"
          value={value ?? 0}
          min={0}
          step={0.001}
          precision={3}
          style={{ width: "100%" }}
          disabled={isOverridden(record)}
          onChange={(v) =>
            updateDimension(record.id, "length", v as number | null)
          }
          onFocus={(e) => e.target.select()}
        />
      ),
    },
    {
      title: "Width",
      dataIndex: "width",
      key: "width",
      width: "9%",
      align: "center" as const,
      render: (value: number | undefined, record: BuildUpItem) => (
        <InputNumber
          size="small"
          value={value ?? 0}
          min={0}
          step={0.001}
          precision={3}
          style={{ width: "100%" }}
          disabled={isOverridden(record)}
          onChange={(v) =>
            updateDimension(record.id, "width", v as number | null)
          }
          onFocus={(e) => e.target.select()}
        />
      ),
    },
    {
      title: "Height",
      dataIndex: "height",
      key: "height",
      width: "9%",
      align: "center" as const,
      render: (value: number | undefined, record: BuildUpItem) => (
        <InputNumber
          size="small"
          value={value ?? 0}
          min={0}
          step={0.001}
          precision={3}
          style={{ width: "100%" }}
          disabled={isOverridden(record)}
          onChange={(v) =>
            updateDimension(record.id, "height", v as number | null)
          }
          onFocus={(e) => e.target.select()}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Net auto-computes as Length × Width × Height. Edit to override; click the Manual badge to revert to auto (which re-enables L/W/H).">
          <span>Net</span>
        </Tooltip>
      ),
      dataIndex: "net",
      key: "net",
      width: "14%",
      align: "center" as const,
      render: (value: number, record: BuildUpItem) => {
        const override = isOverridden(record);
        return (
          <Space.Compact size="small" style={{ width: "100%" }}>
            <InputNumber
              size="small"
              value={value}
              step={0.001}
              precision={3}
              controls={false}
              style={{ width: "100%" }}
              onChange={(v) => updateNet(record.id, v as number | null)}
              onFocus={(e) => e.target.select()}
            />
            <Tooltip
              title={
                override
                  ? "Manual override — click to reset to L × W × H (re-enables Length / Width / Height)"
                  : "Auto = L × W × H"
              }
            >
              <Button
                size="small"
                type={override ? "primary" : "default"}
                onClick={() =>
                  override ? clearOverride(record.id) : undefined
                }
                disabled={!override}
                style={{
                  borderLeft: 0,
                  fontSize: 11,
                  fontWeight: 500,
                  padding: "0 8px",
                }}
              >
                {override ? "Manual" : "Auto"}
              </Button>
            </Tooltip>
          </Space.Compact>
        );
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: "7%",
      align: "center" as const,
      render: (value: string) => formatUnit(value) || "-",
    },
    {
      title: (
        <Tooltip
          title={`Includes Prelims (${specification.prelimsPercent ?? 0}%) + OH&P (${specification.ohpPercent}%)`}
        >
          <span>
            Rate (£){" "}
            <InfoCircleOutlined style={{ fontSize: 11, color: "#8c8c8c" }} />
          </span>
        </Tooltip>
      ),
      dataIndex: "rate",
      key: "rate",
      width: "9%",
      align: "right" as const,
      render: (value: number) => (
        <span>{((value || 0) * markupFactor).toFixed(2)}</span>
      ),
    },
    {
      title: "Total (£)",
      dataIndex: "total",
      key: "total",
      width: "10%",
      align: "right" as const,
      render: (_value: number, record: BuildUpItem) => {
        const markedUpTotal = Math.abs(record.net * record.rate * markupFactor);
        return (
          <span
            className={`font-bold ${markedUpTotal !== 0 ? "text-green-600" : "text-gray-400"}`}
          >
            £{markedUpTotal.toFixed(2)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <Row gutter={[12, 12]} className="mb-4">
        <Col span={6}>
          <Card size="small" className="text-center bg-green-50">
            <Text type="secondary" className="block text-xs mb-1">
              Grand Total
            </Text>
            <Text strong className="text-green-600">
              £{Math.abs(calculatedTotals.grandTotal).toFixed(2)}
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" className="text-center bg-purple-50">
            <Text type="secondary" className="block text-xs mb-1">
              Items Count
            </Text>
            <Text strong className="text-purple-600">
              {items.length}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Main Items Table */}
      <Table
        dataSource={items.map((item) => ({ ...item, key: item.id }))}
        columns={columns}
        pagination={false}
        size="small"
        bordered
        className="geo-tober-wall-buildup-table"
        rowClassName={(_record, index) =>
          index % 2 === 0 ? "bg-gray-25" : "bg-white"
        }
        summary={() => (
          <>
            {/* Grand Total Row (prelims + OH&P baked into rates) */}
            <Table.Summary.Row className="bg-green-50 border-t-2 border-green-200">
              <Table.Summary.Cell
                index={0}
                colSpan={showColumnCodes ? 8 : 7}
                className="text-right pr-4"
              >
                <Text strong className="text-green-700 text-lg">
                  Grand Total:
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={showColumnCodes ? 8 : 7} align="right">
                <Text strong className="text-green-700 text-lg">
                  £{Math.abs(calculatedTotals.grandTotal).toFixed(2)}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        )}
      />

      {/* Additional Information */}
      <Row gutter={16} className="mt-4">
        <Col span={12}>
          <Card size="small" className="bg-gray-50">
            <Text strong className="block mb-2">
              Specification Details:
            </Text>
            <Space direction="vertical" size="small">
              <Text>
                <strong>Wall Type:</strong> {specification.name}
              </Text>
              <Text>
                <strong>Category:</strong> {specification.category}
              </Text>
            </Space>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="bg-gray-50">
            <Space direction="vertical" size="small">
              <div className="flex justify-between border-t pt-1">
                <Text strong>Total Contract Value:&nbsp;</Text>
                <Text strong className="text-green-600">
                  £{Math.abs(calculatedTotals.grandTotal).toFixed(2)}
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Markup note */}
      {/* {markupFactor > 1 && (
        <div style={{ fontSize: 12, color: '#6B7280', padding: '6px 10px', background: '#f9fafb', borderRadius: 4, border: '1px solid #e5e7eb' }}>
          <InfoCircleOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
          Rates include:
          {(specification.prelimsPercent ?? 0) > 0 && <span style={{ marginLeft: 4 }}>Preliminaries <strong>{specification.prelimsPercent}%</strong></span>}
          {(specification.prelimsPercent ?? 0) > 0 && specification.ohpPercent > 0 && <span style={{ margin: '0 4px' }}>+</span>}
          {specification.ohpPercent > 0 && <span>OH&amp;P <strong>{specification.ohpPercent}%</strong></span>}
          <span style={{ marginLeft: 6 }}>(combined markup: <strong>{((markupFactor - 1) * 100).toFixed(2)}%</strong>)</span>
        </div>
      )} */}
    </div>
  );
};

export default GeoToberWallSectionBuildUp;
